import { Button } from "@/components/ui/button/button";
import { SearchInput } from "@/components/ui/input/input";
import { DataTable, DataTableOptions } from "@/components/ui/table/data-table";
import { usePersistedState } from "@/lib/usePersistedState";
import { GetBomItemsResponse, useBom } from "@/queries/bom/use-bom";
import { useOperationBom } from "@/queries/bom/use-operation-bom";
import {
  CellContext,
  ColumnFiltersState,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  HeaderContext,
  OnChangeFn,
  SortingState,
  type ColumnDef,
} from "@tanstack/react-table";
import {
  CheckSquare2Icon,
  MinusIcon,
  PlusIcon,
  SquareIcon,
  XIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BomDocument } from "./bom-document";
import { QueryError } from "@/components/ui/query-error/query-error";
import { cn } from "@/lib/utils";

interface BomItem {
  children?: BomItem[];
  docId?: string;
  item: string;
  itemId: string;
  material: string;
  materialId: string;
  description: string;
  quantity: number;
  uom: string;
}

export function Bom() {
  const { t } = useTranslation();
  const [filter, setFilter] = usePersistedState("bom_filter", "");
  const [showOp, setShowOp] = usePersistedState("bom_show_op", false);
  const [filters, setFilters] = usePersistedState<ColumnFiltersState>(
    "bom_filters",
    []
  );
  const [sorting, setSorting] = usePersistedState<SortingState>(
    "bom_sorting",
    []
  );
  const [expanded, setExpanded] = usePersistedState<ExpandedState>(
    "bom_expanded",
    {}
  );

  const {
    items,
    isPending: isBomLoading,
    error: bomItemsError,
    refetch: refetchBomItems,
  } = useBom();
  const {
    operationItems,
    isPending: isBomOpLoading,
    error: opBomError,
    refetch: refetchOpBom,
  } = useOperationBom();

  const isLoading = showOp ? isBomOpLoading || isBomLoading : isBomLoading;

  const handleExpandedChange = useCallback<OnChangeFn<ExpandedState>>(
    (fn) => {
      if (isLoading) return;
      setExpanded(fn);
    },
    [setExpanded, isLoading]
  );

  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (fn) => {
      if (isLoading) return;
      setSorting(fn);
    },
    [setSorting, isLoading]
  );

  const handleFiltersChange = useCallback<OnChangeFn<ColumnFiltersState>>(
    (fn) => {
      if (isLoading) return;
      setFilters(fn);
    },
    [setFilters, isLoading]
  );

  const data = useMemo(() => {
    if (!showOp) return buildBomTree(items);
    const ids = operationItems.map((i) => i.NId);
    return items
      .filter((i) => ids.includes(i.Material!))
      .map((i) => mapBomItem(i));
  }, [items, operationItems, showOp]);

  const columns = useMemo(() => {
    return [
      ...(showOp
        ? []
        : [
            {
              id: "expander",
              size: 85,
              header: ExpanderHeader,
              cell: ExpanderCell,
              enableSorting: false,
            } satisfies ColumnDef<BomItem, any>,
          ]),
      {
        header: t("bom.table.item"),
        accessorKey: "item",
        size: 100,
        cell: ({ row, column }) => {
          return (
            <span style={{ paddingLeft: `${row.depth * 2}rem` }}>
              {row.getValue(column.id)}
            </span>
          );
        },
      },
      {
        accessorKey: "material",
        header: t("bom.table.material"),
        size: 120,
      },
      {
        id: "document",
        size: 60,
        enableSorting: false,
        cell: ({ row }) => {
          const { docId, materialId } = row.original;
          if (!docId || !materialId) return null;
          return <BomDocument materialId={materialId} />;
        },
      },
      {
        accessorKey: "description",
        header: t("bom.table.description"),
        size: 0,
        cell: ({ row, column }) => {
          return <div className="truncate">{row.getValue(column.id)}</div>;
        },
      },
      {
        accessorKey: "quantity",
        header: t("bom.table.quantity"),
        size: 80,
        enableColumnFilter: false,
      },
      {
        accessorKey: "uom",
        header: t("bom.table.uom"),
        size: 80,
        enableColumnFilter: false,
      },
    ] satisfies ColumnDef<BomItem, any>[];
  }, [showOp, isLoading]);

  const tableOptions: DataTableOptions<BomItem> = {
    data,
    columns,
    defaultColumn: {
      minSize: 0,
    },
    state: {
      sorting,
      expanded,
      columnFilters: filters,
      globalFilter: filter,
    },
    globalFilterFn: "fuzzy",
    filterFromLeafRows: true,
    getSubRows: (row) => row.children ?? [],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: handleSortingChange,
    onExpandedChange: handleExpandedChange,
    onGlobalFilterChange: setFilter,
    onColumnFiltersChange: handleFiltersChange,
  };

  return (
    <div className="flex flex-col gap-2 h-full overflow-hidden">
      <div className="flex items-center gap-x-2">
        <SearchInput
          role="search"
          name="search"
          placeholder={t("bom.search.placeholder")}
          onChange={(e) => setFilter(e.target.value)}
          value={filter}
        />
        <Button
          variant="secondary"
          className="flex gap-x-2 bg-white"
          onClick={() => setFilter("")}
        >
          <XIcon size={18} />
          <span>{t("common.reset")}</span>
        </Button>
        <Button
          variant={showOp ? "default" : "secondary"}
          onClick={() => setShowOp(!showOp)}
          className={cn("flex items-center gap-x-2", !showOp && "bg-white")}
        >
          {showOp ? <CheckSquare2Icon size={18} /> : <SquareIcon size={18} />}
          <span>{t("bom.opBom")}</span>
        </Button>
      </div>
      {opBomError && (
        <QueryError
          resource={t("bom.operationItems")}
          errors={[opBomError]}
          retries={[refetchOpBom]}
        />
      )}
      {bomItemsError ? (
        <QueryError
          resource={t("bom.bomItems")}
          errors={[bomItemsError]}
          retries={[refetchBomItems]}
        />
      ) : (
        <DataTable
          {...tableOptions}
          filter={filter}
          loading={isLoading}
          noResults={t("bom.table.noResults")}
        />
      )}
    </div>
  );
}

function ExpanderHeader({ table }: HeaderContext<BomItem, any>) {
  return (
    <span className="flex items-center justify-end gap-x-2">
      <span
        role="button"
        className="text-primary-foreground rounded-sm border border-primary-foreground cursor-pointer"
        onClick={() => table.toggleAllRowsExpanded(true)}
      >
        <PlusIcon size={17} />
      </span>
      <span
        className="text-primary-foreground rounded-sm border border-primary-foreground cursor-pointer"
        onClick={() => table.toggleAllRowsExpanded(false)}
      >
        <MinusIcon size={17} />
      </span>
    </span>
  );
}

function ExpanderCell({ row }: CellContext<BomItem, any>) {
  if (!row.getCanExpand()) {
    return null;
  }
  return (
    <span
      data-testid="expander"
      className="flex items-center justify-center -translate-x-2"
    >
      <span className="ml-auto text-primary rounded-sm border border-primary ">
        {row.getIsExpanded() ? <MinusIcon size={17} /> : <PlusIcon size={17} />}
      </span>
    </span>
  );
}

function mapBomItem(item: GetBomItemsResponse): BomItem {
  return {
    docId: item.DocumentId ?? "",
    item: item.PositionNumber ?? "",
    itemId: item.BOMItem ?? "",
    material: item.Material ?? "",
    materialId: item.MaterialId ?? "",
    description: item.Description ?? "",
    quantity: item.Quantity ?? 0,
    uom: item.UoM ?? "",
  };
}

function buildBomTree(data: GetBomItemsResponse[]): BomItem[] {
  return data
    .filter((item) => item.IsRoot)
    .map((root): BomItem => {
      return {
        ...mapBomItem(root),
        children: data
          .filter((item) => root.ParentBOM === item.BomId /* don't ask... */)
          .map(mapBomItem),
      };
    });
}
